<template>
  <div class="schedule-wrapper">
    <v-row justify="space-between" class="auction-schedule-row" align="start">
      <v-col
        cols="12"
        :md="isEnglishReverseContinuousType ? 6 : 5"
        class="custom-group pr-md-4 schedule-col form-field"
      >
        <AuctionFormScheduleSelect
          :render-key="startSelectRenderKey"
          :value="startScheduleValue"
          :items="scheduleStartOptions"
          label="Start time *"
          field-id="timer-selector"
          :displayed-text="startSelectDisplayedText"
          @change="onStartScheduleChange"
          @open-specific-dialog="onOpenStartSpecificDialog"
        />

        <AuctionFormSpecificDateTimeDialog
          v-model="startSpecificDialogOpen"
          variant="start"
          :show-errors="showErrors"
          :error-key="startErrorKey"
          :tender-period-end-date-iso="tenderPeriodEndDateIso"
          :date-display="auctionStartDateDisplay"
          @update:dateDisplay="$emit('update:auctionStartDateDisplay', $event)"
          :date-iso="auctionStartDateIso"
          @update:dateIso="$emit('update:auctionStartDateIso', $event)"
          :time-value="auctionStartTime"
          @update:timeValue="$emit('update:auctionStartTime', $event)"
          :date-menu-open="auctionStartDateMenuOpen"
          @update:dateMenuOpen="$emit('update:auctionStartDateMenuOpen', $event)"
          :time-menu-open="auctionStartTimeMenuOpen"
          @update:timeMenuOpen="$emit('update:auctionStartTimeMenuOpen', $event)"
          @save-invalid="onDialogInvalidSave"
          @saved="onSpecificDialogSaved"
        />
        <span v-if="showErrors && activeStartErrorMessage" class="error-input schedule-error">
          {{ activeStartErrorMessage }}
        </span>
      </v-col>
      <v-col
        v-if="isEnglishReverseContinuousType"
        cols="12"
        md="6"
        class="custom-group pl-md-2 schedule-col form-field"
      >
        <AuctionFormScheduleSelect
          :render-key="endSelectRenderKey"
          :value="endScheduleValue"
          :items="scheduleEndOptions"
          label="End time *"
          field-id="end-timer-selector"
          :displayed-text="endSelectDisplayedText"
          @change="onEndScheduleChange"
          @open-specific-dialog="onOpenEndSpecificDialog"
        />

        <AuctionFormSpecificDateTimeDialog
          v-model="endSpecificDialogOpen"
          variant="end"
          :show-errors="showErrors"
          :error-key="endErrorKey"
          :reference-start-schedule-value="startScheduleValue"
          :reference-start-date-iso="auctionStartDateIso"
          :reference-start-time="auctionStartTime"
          :reverse-continuous-specific-min-gap-minutes="reverseContinuousSpecificMinGapMinutes"
          :auction-end-date-picker-min="auctionEndDatePickerMin"
          :auction-end-time-picker-min="auctionEndTimePickerMin"
          :date-display="auctionEndDateDisplay"
          @update:dateDisplay="$emit('update:auctionEndDateDisplay', $event)"
          :date-iso="auctionEndDateIso"
          @update:dateIso="$emit('update:auctionEndDateIso', $event)"
          :time-value="auctionEndTime"
          @update:timeValue="$emit('update:auctionEndTime', $event)"
          :date-menu-open="auctionEndDateMenuOpen"
          @update:dateMenuOpen="$emit('update:auctionEndDateMenuOpen', $event)"
          :time-menu-open="auctionEndTimeMenuOpen"
          @update:timeMenuOpen="$emit('update:auctionEndTimeMenuOpen', $event)"
          @save-invalid="onDialogInvalidSave"
          @saved="onSpecificDialogSaved"
        />
        <span v-if="showErrors && activeEndErrorMessage" class="error-input schedule-error">
          {{ activeEndErrorMessage }}
        </span>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import {
  SCHEDULE_SPECIFIC,
  SCHEDULE_SPECIFIC_LABEL,
  START_SCHEDULE_OPTIONS,
  END_SCHEDULE_OPTIONS,
} from './auctionFormConstants';
import { formatScheduleSpecificLabel, buildScheduleStartMoment } from './dateUtils';
import { getScheduleErrorMessage } from './scheduleValidation';
import AuctionFormScheduleSelect from './AuctionFormScheduleSelect.vue';
import AuctionFormSpecificDateTimeDialog from './AuctionFormSpecificDateTimeDialog.vue';

export default {
  name: 'AuctionFormSchedule',
  components: {
    AuctionFormScheduleSelect,
    AuctionFormSpecificDateTimeDialog,
  },
  props: {
    isEnglishReverseContinuousType: { type: Boolean, required: true },
    reverseContinuousSpecificMinGapMinutes: { type: Number, required: true },
    // Defaults for cases when parent does not provide a schedule yet.
    startScheduleValue: { default: null },
    endScheduleValue: { default: null },
    showErrors: { type: Boolean, default: false },
    startErrorKey: { default: null },
    endErrorKey: { default: null },
    tenderPeriodEndDateIso: {},
    auctionStartDateMenuOpen: { type: Boolean, default: false },
    auctionStartDateIso: {},
    auctionStartDateDisplay: {},
    auctionStartTimeMenuOpen: { type: Boolean, default: false },
    auctionStartTime: {},
    auctionEndDateMenuOpen: { type: Boolean, default: false },
    auctionEndDateIso: {},
    auctionEndDateDisplay: {},
    auctionEndTimeMenuOpen: { type: Boolean, default: false },
    auctionEndTime: {},
    auctionEndDatePickerMin: {},
    auctionEndTimePickerMin: {},
  },
  data() {
    return {
      startSpecificDialogOpen: false,
      endSpecificDialogOpen: false,
      startCommittedSnapshot: null,
      endCommittedSnapshot: null,
      startPreOpenSnapshot: null,
      endPreOpenSnapshot: null,
      startAwaitingSpecificSave: false,
      endAwaitingSpecificSave: false,
    };
  },
  mounted() {
    this.startCommittedSnapshot = this.buildSnapshot('start');
    this.endCommittedSnapshot = this.buildSnapshot('end');
  },
  computed: {
    /** Start moment for duration math (specific date/time or quick offset from now). */
    scheduleStartMoment() {
      return buildScheduleStartMoment(
        this.startScheduleValue,
        this.auctionStartDateIso,
        this.auctionStartTime
      );
    },
    hasStartMomentForDuration() {
      const s = this.scheduleStartMoment;
      return Boolean(s && s.isValid());
    },
    scheduleStartOptions() {
      const specificText = this.isStartSpecific
        ? formatScheduleSpecificLabel(this.auctionStartDateIso, this.auctionStartTime) ||
          SCHEDULE_SPECIFIC_LABEL
        : SCHEDULE_SPECIFIC_LABEL;
      return START_SCHEDULE_OPTIONS.map((o) =>
        o.value === SCHEDULE_SPECIFIC ? { ...o, text: specificText } : o
      );
    },
    scheduleEndOptions() {
      const specificText = this.isEndSpecific
        ? formatScheduleSpecificLabel(this.auctionEndDateIso, this.auctionEndTime) ||
          SCHEDULE_SPECIFIC_LABEL
        : SCHEDULE_SPECIFIC_LABEL;
      return END_SCHEDULE_OPTIONS.map((o) =>
        o.value === SCHEDULE_SPECIFIC ? { ...o, text: specificText } : o
      );
    },
    isStartSpecific() {
      return this.startScheduleValue === SCHEDULE_SPECIFIC;
    },
    isEndSpecific() {
      return this.isEnglishReverseContinuousType && this.endScheduleValue === SCHEDULE_SPECIFIC;
    },
    startScheduleMissing() {
      return this.startScheduleValue === null || this.startScheduleValue === undefined;
    },
    endScheduleMissing() {
      if (!this.isEnglishReverseContinuousType) return false;
      const v = this.endScheduleValue;
      return v === null || v === undefined || (v !== SCHEDULE_SPECIFIC && typeof v !== 'number');
    },
    startSelectDisplayedText() {
      if (this.isStartSpecific) {
        const label = formatScheduleSpecificLabel(this.auctionStartDateIso, this.auctionStartTime);
        return label || SCHEDULE_SPECIFIC_LABEL;
      }
      const opt = this.scheduleStartOptions.find((o) => o.value === this.startScheduleValue);
      return opt ? opt.text : '';
    },
    endSelectDisplayedText() {
      if (this.isEndSpecific) {
        const label = formatScheduleSpecificLabel(this.auctionEndDateIso, this.auctionEndTime);
        return label || SCHEDULE_SPECIFIC_LABEL;
      }
      const opt = this.scheduleEndOptions.find((o) => o.value === this.endScheduleValue);
      return opt ? opt.text : '';
    },
    startSelectRenderKey() {
      if (this.startScheduleValue !== SCHEDULE_SPECIFIC) {
        return `quick-${String(this.startScheduleValue)}`;
      }
      return `spec-${String(this.auctionStartDateIso)}-${String(this.auctionStartTime)}`;
    },
    endSelectRenderKey() {
      if (this.endScheduleValue !== SCHEDULE_SPECIFIC) {
        return `end-quick-${String(this.endScheduleValue)}`;
      }
      return `end-spec-${String(this.auctionEndDateIso)}-${String(this.auctionEndTime)}`;
    },
    activeStartErrorMessage() {
      if (this.startErrorKey) return this.getErrorMessage(this.startErrorKey);
      if (this.startScheduleMissing) return this.getErrorMessage('required_date');
      return null;
    },
    activeEndErrorMessage() {
      if (!this.isEnglishReverseContinuousType) return null;
      if (this.endErrorKey) return this.getErrorMessage(this.endErrorKey);
      if (this.endScheduleMissing) return this.getErrorMessage('required_date');
      return null;
    },
  },
  watch: {
    startScheduleValue(v) {
      this.onScheduleValueWatch('start', v);
    },
    startSpecificDialogOpen(isOpen, wasOpen) {
      this.onSpecificDialogOpenWatch('start', isOpen, wasOpen);
    },

    endScheduleValue(v) {
      if (!this.isEnglishReverseContinuousType) {
        this.resetVariantTransientState('end');
        return;
      }
      this.onScheduleValueWatch('end', v);
    },
    endSpecificDialogOpen(isOpen, wasOpen) {
      this.onSpecificDialogOpenWatch('end', isOpen, wasOpen);
    },
    isEnglishReverseContinuousType(on) {
      if (!on) {
        this.resetVariantTransientState('end');
      }
    },
  },
  methods: {
    getVariantConfig(variant) {
      return variant === 'start'
        ? {
            scheduleValueKey: 'startScheduleValue',
            dateIsoKey: 'auctionStartDateIso',
            dateDisplayKey: 'auctionStartDateDisplay',
            timeValueKey: 'auctionStartTime',
            awaitingKey: 'startAwaitingSpecificSave',
            preOpenKey: 'startPreOpenSnapshot',
            committedKey: 'startCommittedSnapshot',
            dialogOpenKey: 'startSpecificDialogOpen',
            emitScheduleValue: 'update:startScheduleValue',
            emitDateIso: 'update:auctionStartDateIso',
            emitDateDisplay: 'update:auctionStartDateDisplay',
            emitTimeValue: 'update:auctionStartTime',
          }
        : {
            scheduleValueKey: 'endScheduleValue',
            dateIsoKey: 'auctionEndDateIso',
            dateDisplayKey: 'auctionEndDateDisplay',
            timeValueKey: 'auctionEndTime',
            awaitingKey: 'endAwaitingSpecificSave',
            preOpenKey: 'endPreOpenSnapshot',
            committedKey: 'endCommittedSnapshot',
            dialogOpenKey: 'endSpecificDialogOpen',
            emitScheduleValue: 'update:endScheduleValue',
            emitDateIso: 'update:auctionEndDateIso',
            emitDateDisplay: 'update:auctionEndDateDisplay',
            emitTimeValue: 'update:auctionEndTime',
          };
    },
    buildSnapshot(variant, overrides = {}) {
      const config = this.getVariantConfig(variant);
      return {
        scheduleValue: this[config.scheduleValueKey],
        dateIso: this[config.dateIsoKey],
        dateDisplay: this[config.dateDisplayKey],
        timeValue: this[config.timeValueKey],
        ...overrides,
      };
    },
    restoreSnapshot(variant, snapshot) {
      if (!snapshot) return;
      const config = this.getVariantConfig(variant);
      this.$emit(config.emitScheduleValue, snapshot.scheduleValue);
      this.$emit(config.emitDateIso, snapshot.dateIso);
      this.$emit(config.emitDateDisplay, snapshot.dateDisplay);
      this.$emit(config.emitTimeValue, snapshot.timeValue);
    },
    resetVariantTransientState(variant) {
      const config = this.getVariantConfig(variant);
      this[config.awaitingKey] = false;
      this[config.preOpenKey] = null;
      this[config.dialogOpenKey] = false;
    },
    onScheduleValueWatch(variant, value) {
      if (value !== SCHEDULE_SPECIFIC) {
        this.resetVariantTransientState(variant);
      }
    },
    onSpecificDialogOpenWatch(variant, isOpen, wasOpen) {
      if (!wasOpen || isOpen) return;
      const config = this.getVariantConfig(variant);
      if (this[config.awaitingKey]) {
        const rollbackSnapshot =
          this[config.preOpenKey] !== null ? this[config.preOpenKey] : this[config.committedKey];
        this.restoreSnapshot(variant, rollbackSnapshot);
        this[config.awaitingKey] = false;
      }
      this[config.preOpenKey] = null;
    },
    onOpenSpecificDialog(variant) {
      const config = this.getVariantConfig(variant);
      this[config.awaitingKey] = true;
      this[config.dialogOpenKey] = true;
    },
    onOpenStartSpecificDialog() {
      this.onOpenSpecificDialog('start');
    },
    onOpenEndSpecificDialog() {
      this.onOpenSpecificDialog('end');
    },
    onSpecificDialogSaved({ variant, dateDisplay, dateIso, timeValue }) {
      const config = this.getVariantConfig(variant);
      this[config.awaitingKey] = false;
      this[config.preOpenKey] = null;
      this[config.committedKey] = {
        scheduleValue: SCHEDULE_SPECIFIC,
        dateIso,
        dateDisplay,
        timeValue,
      };
    },
    onDialogInvalidSave() {
      this.$emit('request-show-errors');
    },
    getErrorMessage(key) {
      return getScheduleErrorMessage(key, {
        reverseContinuousSpecificMinGapMinutes: this.reverseContinuousSpecificMinGapMinutes,
      });
    },
    onScheduleChange(variant, v) {
      const config = this.getVariantConfig(variant);
      const wasSpecific = this[config.scheduleValueKey] === SCHEDULE_SPECIFIC;
      if (v === SCHEDULE_SPECIFIC) {
        this[config.preOpenKey] = this.buildSnapshot(variant);
        this[config.awaitingKey] = true;
      }
      this.$emit(config.emitScheduleValue, v);
      if (v !== SCHEDULE_SPECIFIC) {
        this[config.awaitingKey] = false;
        this[config.preOpenKey] = null;
        if (wasSpecific) {
          this.$emit(config.emitDateIso, null);
          this.$emit(config.emitDateDisplay, null);
          this.$emit(config.emitTimeValue, null);
          this[config.committedKey] = {
            scheduleValue: v,
            dateIso: null,
            dateDisplay: null,
            timeValue: null,
          };
          return;
        }
        this[config.committedKey] = {
          scheduleValue: v,
          dateIso: this[config.dateIsoKey],
          dateDisplay: this[config.dateDisplayKey],
          timeValue: this[config.timeValueKey],
        };
      }
    },
    onStartScheduleChange(v) {
      this.onScheduleChange('start', v);
    },
    onEndScheduleChange(v) {
      this.onScheduleChange('end', v);
    },
  },
};
</script>

<style scoped>
.schedule-wrapper {
  padding-bottom: 14px;
}

.auction-schedule-row {
  margin-top: 6px;
  margin-bottom: 4px;
}

.schedule-col {
  display: flex;
  flex-direction: column;
  margin-bottom: 8px;
}

.schedule-error {
  margin-top: 20px;
  line-height: 1.3;
  white-space: normal;
  word-break: break-word;
}
</style>
